import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { NoticeSignal } from '@xanadu/signal';
import { Node, NodeModel } from '@xanadu/model';
import { Observable, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';

import { SafeStyle } from '@angular/platform-browser';
import { DataService } from '@xanadu/service';
import { DynamicComponent } from '../dynamic/dynamic.component';
import { TabSignal, TabSignalSet, TreeSignal } from '@xanadu/signal';

@Component({
    selector: 'node-view',
    template: '',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[style]': 'hostStyle'
    }
})
export abstract class NodeViewComponent<T extends Node> extends DynamicComponent {
    protected readonly noticeSignal = inject(NoticeSignal);
    protected readonly treeSignal = inject(TreeSignal);
    protected readonly tabSignal = inject(TabSignal);

    protected readonly dataService = inject(DataService);

    protected readonly view = signal<any>(undefined);

    hostStyle?: SafeStyle;
    nodeModel?: NodeModel;

    protected get nodeType(): string | undefined {
        return undefined;
    }

    constructor() {
        super();


        effect(() => {
            const model = this.model();

            if (model?.hostStyle) {
                this.hostStyle = model.hostStyle;
            }
        });

        effect(() => {
            const model = this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>]();

            if (model) {
                // queueMicrotask is used to ensure that the panel can finish saving the previous node before loading the next one.
                queueMicrotask(() => {
                    void this.loadNode(model);
                });
            }
        });

        effect(() => {
            const model = this.tabSignal.delete[this.nodeType as keyof TabSignalSet<NodeModel>]();

            if (model) {
                void firstValueFrom(this.remove(model)).then(node => {
                    if (node?._id?.length) {
                        this.tabSignal.erase[
                            this.nodeType as keyof TabSignalSet<string>
                        ].set(node._id);
                    }
                });
            }
        });
    }

    protected remove(model: NodeModel): Observable<T> {
        return this.delete(model.node)
            .pipe(tap(result => {
                model.delete();

                if (result) {
                    this.noticeSignal.info(`Node ${result.name} is successfully deleted.`);
                }
            }));
    }

    protected abstract getPanel(model: NodeModel): any;
    protected abstract delete(node: Node): Observable<T>;

    private async loadNode(model: NodeModel): Promise<void> {
        if (model && !model.loaded && model.node.parent && !model.node.isNew && model.node._id) {
            const node = void firstValueFrom(this.dataService.getNode(model.node._id))
                .then(node => {
                    if (node) {
                        model.load(node);
                    }

                    this.tabSignal.load[this.nodeType as keyof TabSignalSet<NodeModel>].set(model);
                    this.setView(model);
                });
        } else {
            this.setView(model);
        }
    }

    private setView(model: NodeModel): void {
        if (this.nodeModel !== model) {
            this.nodeModel = model;
            if (model) {
                const panel = this.getPanel(model);
                this.view.set(panel);
            }
        }
    }
}
